import {
  AccessTime,
  CalendarToday,
  Event,
  PersonAdd,
  Refresh,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { CheckCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { getAllEvents } from "../../../services/event.service";
import { getUserOnly } from "../../../services/expositor/subscriptions.service";
import { ModalComponent, useModal } from "../../../ui-components/Modal";
import { parseDate, parseDateLocale } from "../../../utils/parseDate";
import UpdateSubscriptionForm from "../PreSubscriptions/UpdateSubscriptionForm";
import CreateSubscriptionForm from "./CreateSubscriptionForm";

const createDateList = (events: any): String[] => {
  const evArr = [];
  events.forEach((event: any) => {
    evArr.push(parseDate(event.starts_at));
    evArr.push(parseDate(event.ends_at));
  });
  return evArr;
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [dates, setDates] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState({});
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(
    null,
  );
  const [userSubscriptions, setUserSubscriptions] = React.useState([]);
  const [loading, setLoading] = useState(false);

  async function syncSubscriptions() {
    setUserSubscriptions(await getUserOnly());
  }

  React.useEffect(() => {
    syncSubscriptions();
  }, []);

  const { open, handleOpen, handleClose } = useModal();

  function syncEvents() {
    setLoading(true);
    getAllEvents().then((eventsArr) => {
      const activeEvents = eventsArr.filter((x) => x.active);
      setEvents(activeEvents);
      setFilteredEvents(activeEvents);
      setLoading(false);
    });
  }

  const handleDateChange = (date: Date) => {
    setSelectedCalendarDate(date);
    try {
      const dateString = date.toISOString().split("T")[0];
      const eventsForDate = events.filter((event: any) => {
        try {
          const eventStartDate = new Date(event.starts_at)
            .toISOString()
            .split("T")[0];
          const eventEndDate = new Date(event.ends_at)
            .toISOString()
            .split("T")[0];
          return dateString >= eventStartDate && dateString <= eventEndDate;
        } catch {
          return false;
        }
      });
      setFilteredEvents(eventsForDate);
    } catch {
      setFilteredEvents([]);
    }
  };

  const clearDateFilter = () => {
    setSelectedCalendarDate(null);
    setFilteredEvents(events);
  };

  const getEventsForDate = (date: Date) => {
    try {
      const dateString = date.toISOString().split("T")[0];
      return events.filter((event: any) => {
        try {
          const eventStartDate = new Date(event.starts_at)
            .toISOString()
            .split("T")[0];
          const eventEndDate = new Date(event.ends_at)
            .toISOString()
            .split("T")[0];
          return dateString >= eventStartDate && dateString <= eventEndDate;
        } catch {
          return false;
        }
      });
    } catch {
      return [];
    }
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view === "month") {
      const eventsForDate = getEventsForDate(date);
      if (eventsForDate.length > 0) {
        return (
          <div
            style={{
              backgroundColor: "#1976d2",
              borderRadius: "50%",
              width: "6px",
              height: "6px",
              margin: "0 auto",
              marginTop: "2px",
            }}
          />
        );
      }
    }
    return null;
  };

  useEffect(() => {
    syncEvents();
  }, []);

  useEffect(() => {
    setDates(createDateList(events));
  }, [events]);

  return (
    <>
      <ModalComponent
        open={open}
        handleClose={handleClose}
        title={
          isUpdateMode
            ? "Alterar pré-inscrição"
            : "Criar pré-inscrição para evento"
        }
      >
        <Box sx={{ p: 3 }}>
          {isUpdateMode ? (
            <UpdateSubscriptionForm
              selectedSub={selectedSubscription}
              handleClose={handleClose}
              events={events}
            />
          ) : (
            <CreateSubscriptionForm
              date={selectedDate}
              events={events}
              handleClose={handleClose}
              event={selectedEvent}
            />
          )}
        </Box>
      </ModalComponent>
      <Box sx={{ p: 0, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#24324D",
              mb: 1,
            }}
          >
            Eventos
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#64748b",
              mb: 3,
            }}
          >
            Gerencie suas pré-inscrições e acompanhe os próximos eventos
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Calendar Section */}
          <Grid item xs={12} md={5}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow:
                  "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
                border: "1px solid #e2e8f0",
                overflow: "visible",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <CalendarToday sx={{ color: "#24324D", mr: 2 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#24324D",
                      flex: 1,
                    }}
                  >
                    Calendário de Eventos
                  </Typography>
                  {selectedCalendarDate && (
                    <Button
                      onClick={clearDateFilter}
                      size="small"
                      sx={{
                        color: "#64748b",
                        fontSize: "0.75rem",
                        textTransform: "none",
                      }}
                    >
                      Limpar Filtro
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    "& .react-calendar": {
                      border: "none",
                      borderRadius: "12px",
                      backgroundColor: "#ffffff",
                      fontFamily: "inherit",
                      "& .react-calendar__tile": {
                        borderRadius: "8px",
                        border: "none",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      },
                      "& .react-calendar__tile--active": {
                        backgroundColor: "#24324D !important",
                        color: "#fff",
                      },
                    },
                  }}
                >
                  <Calendar
                    showNavigation={true}
                    value={selectedCalendarDate || new Date()}
                    onChange={handleDateChange}
                    tileContent={tileContent}
                    onClickDay={(value, event) => {
                      if (dates.includes(parseDate(value))) {
                        setSelectedDate(value);
                      }
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Next Event Section */}
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow:
                  "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
                border: "1px solid #e2e8f0",
                background: "linear-gradient(135deg, #24324D 0%, #1a2538 100%)",
                color: "white",
                overflow: "visible",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Event sx={{ color: "white", mr: 2 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "white",
                    }}
                  >
                    Próximo Evento
                  </Typography>
                </Box>

                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress sx={{ color: "white" }} />
                  </Box>
                ) : events.length > 0 ? (
                  <>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: "white",
                      }}
                    >
                      {events[0].name} - {parseDate(events[0].starts_at)}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mb: 3,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <AccessTime
                          sx={{
                            fontSize: 18,
                            mr: 1,
                            color: "rgba(255,255,255,0.8)",
                          }}
                        />
                        <Typography sx={{ color: "rgba(255,255,255,0.9)" }}>
                          Início: {parseDateLocale(events[0].starts_at)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <AccessTime
                          sx={{
                            fontSize: 18,
                            mr: 1,
                            color: "rgba(255,255,255,0.8)",
                          }}
                        />
                        <Typography sx={{ color: "rgba(255,255,255,0.9)" }}>
                          Fim: {parseDateLocale(events[0].ends_at)}
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      disabled={
                        userSubscriptions.find(
                          (s) => s.event_id === events[0].id,
                        )?.status_id === 1
                      }
                      startIcon={
                        userSubscriptions.find(
                          (s) => s.event_id === events[0].id,
                        ) ? (
                          userSubscriptions.find(
                            (s) => s.event_id === events[0].id,
                          )?.status_id === 1 ? (
                            <CheckCircle />
                          ) : (
                            <Refresh />
                          )
                        ) : (
                          <PersonAdd />
                        )
                      }
                      onClick={() => {
                        const existingSub = userSubscriptions.find(
                          (s) => s.event_id === events[0].id,
                        );
                        if (existingSub?.status_id === 1) return;
                        if (existingSub) {
                          setSelectedSubscription(existingSub);
                          setIsUpdateMode(true);
                        } else {
                          setSelectedSubscription(null);
                          setIsUpdateMode(false);
                          setSelectedDate(events[0].starts_at);
                        }
                        setSelectedEvent(events[0]);
                        handleOpen();
                      }}
                      sx={{
                        backgroundColor: "white",
                        color: "#24324D",
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                        transition: "all 0.2s ease-in-out",
                        "&:disabled": {
                          backgroundColor: "#e2e8f0",
                          color: "#94a3b8",
                        },
                      }}
                    >
                      {userSubscriptions.find(
                        (s) => s.event_id === events[0].id,
                      )
                        ? userSubscriptions.find(
                            (s) => s.event_id === events[0].id,
                          )?.status_id === 1
                          ? "Você já está inscrito"
                          : "Alterar pré-inscrição"
                        : "Quero me pré-inscrever"}
                    </Button>
                  </>
                ) : (
                  <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                    Nenhum evento disponível no momento
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Events List Section */}
        <Card
          sx={{
            mt: 4,
            borderRadius: 3,
            boxShadow:
              "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
            border: "1px solid #e2e8f0",
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: "1px solid #e2e8f0" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Event sx={{ color: "#24324D", mr: 2 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#24324D",
                    }}
                  >
                    Lista de Eventos
                  </Typography>
                </Box>
                <IconButton
                  onClick={syncEvents}
                  sx={{
                    color: "#24324D",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                    },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <Box sx={{ p: 2, display: "grid", gap: 2 }}>
                    {filteredEvents &&
                      filteredEvents
                        .filter(
                          (event) =>
                            !userSubscriptions
                              .filter((sub) => sub.status_id === 2)
                              .find((sub) => sub.event_id === event.id),
                        )
                        .map((row) => (
                          <Box
                            key={`${row.id}-${row.starts_at}`}
                            sx={{
                              border: "1px solid #e2e8f0",
                              borderRadius: 2,
                              p: 2,
                              backgroundColor: "#ffffff",
                              boxShadow:
                                "0 1px 2px rgba(0,0,0,0.06), 0 1px 1px rgba(0,0,0,0.04)",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Event sx={{ color: "#24324D", mr: 1 }} />
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: "#1e293b",
                                  flex: 1,
                                }}
                              >
                                {row.name}
                              </Typography>
                              <Chip
                                label="Ativo"
                                size="small"
                                sx={{
                                  backgroundColor: "#dcfce7",
                                  color: "#166534",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                }}
                              />
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                sx={{ color: "#475569", fontWeight: 500 }}
                              >
                                {parseDateLocale(row.starts_at)}
                              </Typography>
                              <Typography
                                sx={{ color: "#64748b", fontSize: "0.825rem" }}
                              >
                                até {parseDateLocale(row.ends_at)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                mt: 1.5,
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                disabled={
                                  userSubscriptions.find(
                                    (s) => s.event_id === row.id,
                                  )?.status_id === 1
                                }
                                onClick={() => {
                                  const existingSub = userSubscriptions.find(
                                    (s) => s.event_id === row.id,
                                  );
                                  if (existingSub?.status_id === 1) return;
                                  if (existingSub) {
                                    setSelectedSubscription(existingSub);
                                    setIsUpdateMode(true);
                                  } else {
                                    setSelectedSubscription(null);
                                    setIsUpdateMode(false);
                                    setSelectedDate(row.starts_at);
                                  }
                                  handleOpen();
                                  setSelectedEvent(row);
                                }}
                                variant="contained"
                                startIcon={
                                  userSubscriptions.find(
                                    (s) => s.event_id === row.id,
                                  ) ? (
                                    userSubscriptions.find(
                                      (s) => s.event_id === row.id,
                                    )?.status_id === 1 ? (
                                      <CheckCircle />
                                    ) : (
                                      <Refresh />
                                    )
                                  ) : (
                                    <PersonAdd />
                                  )
                                }
                                sx={{
                                  backgroundColor: "#24324D",
                                  color: "white",
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  px: 2,
                                  py: 1,
                                  fontSize: "0.875rem",
                                  "&:hover": {
                                    backgroundColor: "#1a2538",
                                    transform: "translateY(-1px)",
                                    boxShadow:
                                      "0 4px 12px rgba(36, 50, 77, 0.3)",
                                  },
                                  transition: "all 0.2s ease-in-out",
                                  "&:disabled": {
                                    backgroundColor: "#e2e8f0",
                                    color: "#94a3b8",
                                  },
                                }}
                              >
                                {userSubscriptions.find(
                                  (s) => s.event_id === row.id,
                                )
                                  ? userSubscriptions.find(
                                      (s) => s.event_id === row.id,
                                    )?.status_id === 1
                                    ? "Inscrito"
                                    : "Alterar"
                                  : "Pré-inscrever"}
                              </Button>
                            </Box>
                          </Box>
                        ))}
                  </Box>
                </Box>

                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                            Nome do Evento
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 600, color: "#475569" }}
                          >
                            Data
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 600, color: "#475569" }}
                          >
                            Ações
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredEvents &&
                          filteredEvents
                            .filter(
                              (event) =>
                                !userSubscriptions
                                  .filter((sub) => sub.status_id === 2)
                                  .find((sub) => sub.event_id === event.id),
                            )
                            .map((row, index) => (
                              <TableRow
                                key={row.name}
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                  "&:hover": {
                                    backgroundColor: "#f8fafc",
                                  },
                                  borderBottom:
                                    index === events.length - 1
                                      ? "none"
                                      : "1px solid #e2e8f0",
                                }}
                              >
                                <TableCell component="th" scope="row">
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#1e293b",
                                        mb: 0.5,
                                      }}
                                    >
                                      {`${row.name} - ${parseDate(row.starts_at)}`}
                                    </Typography>
                                    <Chip
                                      label="Ativo"
                                      size="small"
                                      sx={{
                                        backgroundColor: "#dcfce7",
                                        color: "#166534",
                                        fontWeight: 500,
                                        fontSize: "0.75rem",
                                      }}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ textAlign: "right" }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#475569",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {parseDateLocale(row.starts_at)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#64748b",
                                        display: "block",
                                      }}
                                    >
                                      até {parseDateLocale(row.ends_at)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    disabled={
                                      userSubscriptions.find(
                                        (s) => s.event_id === row.id,
                                      )?.status_id === 1
                                    }
                                    onClick={() => {
                                      const existingSub =
                                        userSubscriptions.find(
                                          (s) => s.event_id === row.id,
                                        );
                                      if (existingSub?.status_id === 1) return;
                                      if (existingSub) {
                                        setSelectedSubscription(existingSub);
                                        setIsUpdateMode(true);
                                      } else {
                                        setSelectedSubscription(null);
                                        setIsUpdateMode(false);
                                        setSelectedDate(row.starts_at);
                                      }
                                      handleOpen();
                                      setSelectedEvent(row);
                                    }}
                                    variant="contained"
                                    startIcon={
                                      userSubscriptions.find(
                                        (s) => s.event_id === row.id,
                                      ) ? (
                                        userSubscriptions.find(
                                          (s) => s.event_id === row.id,
                                        )?.status_id === 1 ? (
                                          <CheckCircle />
                                        ) : (
                                          <Refresh />
                                        )
                                      ) : (
                                        <PersonAdd />
                                      )
                                    }
                                    sx={{
                                      backgroundColor: "#24324D",
                                      color: "white",
                                      fontWeight: 600,
                                      borderRadius: 2,
                                      px: 2,
                                      py: 1,
                                      fontSize: "0.875rem",
                                      "&:hover": {
                                        backgroundColor: "#1a2538",
                                        transform: "translateY(-1px)",
                                        boxShadow:
                                          "0 4px 12px rgba(36, 50, 77, 0.3)",
                                      },
                                      transition: "all 0.2s ease-in-out",
                                      "&:disabled": {
                                        backgroundColor: "#e2e8f0",
                                        color: "#94a3b8",
                                      },
                                    }}
                                  >
                                    {userSubscriptions.find(
                                      (s) => s.event_id === row.id,
                                    )
                                      ? userSubscriptions.find(
                                          (s) => s.event_id === row.id,
                                        )?.status_id === 1
                                        ? "Inscrito"
                                        : "Alterar"
                                      : "Pré-inscrever"}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {filteredEvents &&
                  filteredEvents.filter(
                    (event) =>
                      !userSubscriptions
                        .filter((sub) => sub.status_id === 2)
                        .find((sub) => sub.event_id === event.id),
                  ).length === 0 && (
                    <Box sx={{ p: 6, textAlign: "center" }}>
                      <Event sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#64748b",
                          mb: 1,
                          fontWeight: 500,
                        }}
                      >
                        Nenhum evento disponível
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#94a3b8",
                        }}
                      >
                        Não há eventos disponíveis para inscrição no momento
                      </Typography>
                    </Box>
                  )}
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
